import { Alert, Button, Card, Input, Modal, Space, Typography, message } from "antd";
import type { DeviceVo } from "@zhihaoscm/entity";
import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { ABLE } from "@/meta/common";
import { DEVICE_TYPE, ONLINE } from "@/meta/logistics/device";
import { createPlayer, Events, type H265Player } from "@/utils/h265Player";
import { useUserStore } from "@/views/stores";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid
} from "zhihao-ui-react";

export interface DeviceManagementDetailPageProps {
    deviceId?: number | string;
    loadDevice?: (id: number | string) => Promise<DeviceVo | undefined>;
    toggleDeviceState?: (id: number, status: number) => Promise<unknown>;
    deleteDevice?: (id: number) => Promise<unknown>;
    monitorDevice?: (detail: DeviceVo) => Promise<MonitorStream | undefined>;
    loadMonitorStream?: (detail: DeviceVo) => Promise<MonitorStream | undefined>;
    loadPlaybackStream?: (detail: DeviceVo, playbackAt?: string) => Promise<MonitorStream | undefined>;
    stopMonitorStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    stopPlaybackStream?: (serialNo: string, liveId: string) => Promise<unknown>;
    banButton?: boolean;
}

export interface MonitorStream {
    playUrl?: string;
    liveId?: string;
}

/** 复用 Vue 设备详情接口，保持 `/device/vo/:id` 返回结构。 */
export async function loadDeviceManagementDetail(id: number | string) {
    const response = await shippingRequestHttp.service.get<DeviceVo>(`/device/vo/${id}`);
    return response.data;
}

/** 复用 Vue 设备启用/禁用接口。 */
export async function toggleDeviceManagementDetailState(id: number, status: number) {
    const response = await shippingRequestHttp.service.put(`/device/update/${id}/state/${status}`);
    return response.data;
}

/** 复用 Vue 设备删除接口。 */
export async function deleteDeviceManagementDetail(id: number) {
    const response = await shippingRequestHttp.service.delete(`/device/${id}`);
    return response.data;
}

/** 复用 Vue `deviceService.getStandardPlayUrl` 的直播取流接口。 */
export async function loadDeviceMonitorStream(detail: DeviceVo) {
    const response = await shippingRequestHttp.service.get<DeviceVo>("/device/media/video", {
        params: { deviceSerial: detail.device.serialNo },
        timeout: 30000
    });
    return { playUrl: response.data.playUrl, liveId: response.data.liveId };
}

/** 复用 Vue `playback.vue` 的回放取流接口，固定使用 type=2 和当天时间范围。 */
export async function loadDevicePlaybackStream(detail: DeviceVo, playbackAt?: string) {
    const { startTime, endTime } = getPlaybackTimeRange(playbackAt);
    const response = await shippingRequestHttp.service.get<DeviceVo>("/device/media/video", {
        params: {
            deviceSerial: detail.device.serialNo,
            type: 2,
            startTime,
            endTime
        },
        timeout: 30000
    });
    return { playUrl: response.data.playUrl, liveId: response.data.liveId };
}

/** 复用 Vue `deviceService.stopPlay` 的直播停止接口。 */
export async function stopDeviceMonitorStream(serialNo: string, liveId: string) {
    const response = await shippingRequestHttp.service.put(`/device/stop/${serialNo}?type=PLAY&liveId=${liveId}`);
    return response.data;
}

/** 复用 Vue `playback.vue` 的回放停止接口，使用 PLAYBACK 流类型。 */
export async function stopDevicePlaybackStream(serialNo: string, liveId: string) {
    const response = await shippingRequestHttp.service.put(`/device/stop/${serialNo}?type=PLAYBACK&liveId=${liveId}`);
    return response.data;
}

const valueOrDefault = (value?: string | number) => (value == null || value === "" ? DEFAULT_VALUE : String(value));
const formatDate = (value?: string) => (value ? value.replace("T", " ").slice(0, 16) : DEFAULT_VALUE);

type MonitorPlaybackState = "pending" | "playing" | "paused" | "stopped" | "error";
const playbackStateLabels: Record<MonitorPlaybackState, string> = {
    pending: "待播放",
    playing: "播放中",
    paused: "已暂停",
    stopped: "已停止",
    error: "播放失败"
};

/** 格式化回放时间轴的秒数，和 Vue timeLine 的当天时间范围保持一致。 */
const formatPlaybackSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(rest)}`;
};

const getLocalDateKey = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** 生成 Vue 回放接口要求的本地当天起止时间，避免引入旧工具模块的 Vue 类型依赖。 */
const getPlaybackTimeRange = (playbackAt?: string) => {
    const parsed = playbackAt ? new Date(playbackAt.replace(" ", "T")) : new Date();
    const now = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
    const startAt =
        playbackAt && !Number.isNaN(parsed.getTime())
            ? `${getLocalDateKey(now)}T${formatPlaybackSeconds(now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds())}`
            : `${getLocalDateKey(now)}T00:00:00`;
    return { startTime: startAt, endTime: `${getLocalDateKey(now)}T23:59:59` };
};

/** 物流管理-设备详情，覆盖 Vue Scan 分支的基础和安装信息。 */
export function DeviceManagementDetailPage({
    deviceId,
    loadDevice = loadDeviceManagementDetail,
    toggleDeviceState = toggleDeviceManagementDetailState,
    deleteDevice = deleteDeviceManagementDetail,
    monitorDevice,
    loadMonitorStream = loadDeviceMonitorStream,
    loadPlaybackStream = loadDevicePlaybackStream,
    stopMonitorStream = stopDeviceMonitorStream,
    stopPlaybackStream = stopDevicePlaybackStream,
    banButton
}: DeviceManagementDetailPageProps) {
    const routeParams = useParams<{ id: string }>();
    const navigate = useNavigate();
    // 对齐旧 Vue userStore.banButton，同时允许宿主显式传入封禁状态。
    const storeBanButton = useUserStore(state => state.banButton);
    const isButtonBanned = banButton ?? storeBanButton;
    const resolvedId = deviceId ?? routeParams.id ?? "";
    const [detail, setDetail] = useState<DeviceVo>();
    const [errorMessage, setErrorMessage] = useState("");
    const [pendingState, setPendingState] = useState<number>();
    const [actionLoading, setActionLoading] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteSerial, setDeleteSerial] = useState("");
    const [deleteValidationError, setDeleteValidationError] = useState("");
    const [monitorOpen, setMonitorOpen] = useState(false);
    const [monitorLoading, setMonitorLoading] = useState(false);
    const [monitorStream, setMonitorStream] = useState<MonitorStream>();
    const [monitorMode, setMonitorMode] = useState<"live" | "playback">("live");
    const [monitorError, setMonitorError] = useState("");
    const [monitorRetryKey, setMonitorRetryKey] = useState(0);
    const [monitorPlaybackState, setMonitorPlaybackState] = useState<MonitorPlaybackState>("pending");
    const [playbackPosition, setPlaybackPosition] = useState(0);
    const [monitorVideoReady, setMonitorVideoReady] = useState(false);
    const monitorVideoRef = useRef<HTMLVideoElement>(null);
    const setMonitorVideoRef = useCallback((element: HTMLVideoElement | null) => {
        monitorVideoRef.current = element;
        setMonitorVideoReady(Boolean(element));
    }, []);

    useEffect(() => {
        let disposed = false;
        void loadDevice(resolvedId)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("设备详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDevice, resolvedId]);

    const device = detail?.device;
    const ship = detail?.ship;
    const shipName = ship?.cnname || ship?.name;
    const nextState = device?.state === ABLE.ENABLE.code ? ABLE.DISABLE.code : ABLE.ENABLE.code;
    const canMonitor = device?.type === DEVICE_TYPE.CAMERA.code;

    const handleStateConfirm = async () => {
        if (pendingState == null || device?.id == null) return;
        setActionLoading(true);
        try {
            await toggleDeviceState(Number(device.id), pendingState);
            message.success(pendingState === ABLE.ENABLE.code ? "已启用" : "已禁用");
            setPendingState(undefined);
            const refreshed = await loadDevice(resolvedId);
            setDetail(refreshed);
        } catch {
            setErrorMessage("设备状态更新失败");
        } finally {
            setActionLoading(false);
        }
    };

    /** 删除前沿用 Vue 的序列号二次确认，并拦截已绑定船舶的设备。 */
    const handleDeleteConfirm = async () => {
        if (device?.id == null) return;
        if (ship?.id) {
            setDeleteDialogOpen(false);
            setErrorMessage("该设备已绑定船舶，请先解绑船舶！");
            return;
        }
        if (deleteSerial !== device.serialNo) {
            setDeleteValidationError("序列号校验失败");
            return;
        }
        setActionLoading(true);
        try {
            await deleteDevice(Number(device.id));
            message.success("删除成功");
            setDeleteDialogOpen(false);
            navigate(-1);
        } catch {
            setErrorMessage("删除失败");
        } finally {
            setActionLoading(false);
        }
    };

    /** 按 Vue 规则仅为摄像头打开监控承载窗口。 */
    const handleMonitor = async () => {
        if (!detail) return;
        setMonitorLoading(true);
        setMonitorMode("live");
        setMonitorError("");
        setMonitorPlaybackState("pending");
        setPlaybackPosition(0);
        try {
            const stream = await (monitorDevice ? monitorDevice(detail) : loadMonitorStream(detail));
            setMonitorStream(stream);
            setMonitorOpen(true);
        } catch {
            // 取流失败时同步更新弹窗错误状态，复用 Vue video-dialog 的重试入口。
            setMonitorError("监控加载失败");
            setErrorMessage("监控加载失败");
        } finally {
            setMonitorLoading(false);
        }
    };

    /** 按 Vue video-dialog 约定切换直播和回放，并让播放器重新绑定新流。 */
    const handleMonitorModeSwitch = async () => {
        if (!detail || monitorLoading) return;
        const nextMode = monitorMode === "live" ? "playback" : "live";
        setMonitorLoading(true);
        try {
            const stream = nextMode === "playback" ? await loadPlaybackStream(detail) : await loadMonitorStream(detail);
            setMonitorMode(nextMode);
            setMonitorError("");
            setMonitorPlaybackState("pending");
            setPlaybackPosition(0);
            setMonitorStream(stream);
        } catch {
            // 模式切换取流失败时保留在弹窗内，提供 Vue video-dialog 的重试入口。
            setMonitorError("监控加载失败");
            setErrorMessage("监控加载失败");
        } finally {
            setMonitorLoading(false);
        }
    };

    /** 播放失败后沿用 Vue 播放器的恢复思路，销毁旧实例并重新加载当前流。 */
    const handleMonitorRetry = () => {
        setMonitorError("");
        setErrorMessage(current => (current === "监控播放失败" ? "" : current));
        setMonitorRetryKey(current => current + 1);
    };

    /** 迁移 Vue timeLine 的选时行为：暂停当前回放并按选中秒数重新取流。 */
    const handlePlaybackTimelineChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const seconds = Number(event.target.value);
        setPlaybackPosition(seconds);
        setMonitorPlaybackState("stopped");
        if (!detail || monitorMode !== "playback" || monitorLoading) return;
        setMonitorLoading(true);
        try {
            const playbackAt = `${getLocalDateKey(new Date())} ${formatPlaybackSeconds(seconds)}`;
            const stream = await loadPlaybackStream(detail, playbackAt);
            setMonitorStream(stream);
            setMonitorError("");
        } catch {
            setMonitorError("监控加载失败");
            setErrorMessage("监控加载失败");
        } finally {
            setMonitorLoading(false);
        }
    };

    /** 监听原生 video 事件，补齐 Vue Plyr 的播放、暂停和结束状态反馈。 */
    useEffect(() => {
        if (!monitorOpen || monitorMode !== "playback" || !monitorVideoReady || !monitorVideoRef.current) return;
        const video = monitorVideoRef.current;
        const onPlay = () => setMonitorPlaybackState("playing");
        const onPause = () => setMonitorPlaybackState("paused");
        const onEnded = () => setMonitorPlaybackState("stopped");
        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("ended", onEnded);
        return () => {
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("ended", onEnded);
        };
    }, [monitorMode, monitorOpen, monitorVideoReady]);

    /** 直播弹窗生命周期内创建/销毁 H265 播放器，并按 Vue 契约停止直播流。 */
    useEffect(() => {
        if (!monitorOpen || !monitorStream?.playUrl) return;
        let player: H265Player | undefined;
        if (monitorVideoReady && monitorVideoRef.current && typeof globalThis.MediaSource !== "undefined") {
            player = createPlayer({
                type: "flv",
                isLive: true,
                hasAudio: false,
                hasVideo: true,
                url: monitorStream.playUrl
            });
            player.attachMediaElement(monitorVideoRef.current);
            player.on(Events.ERROR, () => {
                setMonitorError("监控播放失败");
                setMonitorPlaybackState("error");
                setErrorMessage("监控播放失败");
            });
            player.load();
            void player
                .play()
                .then(() => {
                    if (monitorMode === "playback") setMonitorPlaybackState("playing");
                })
                .catch(() => {
                    setMonitorPlaybackState("error");
                    setMonitorError("监控播放失败");
                    setErrorMessage("监控播放失败");
                });
        }
        return () => {
            player?.unload();
            player?.destroy();
            if (device?.serialNo && monitorStream.liveId) {
                const stopStream = monitorMode === "playback" ? stopPlaybackStream : stopMonitorStream;
                void stopStream(device.serialNo, monitorStream.liveId).catch(() => undefined);
            }
        };
    }, [
        device?.serialNo,
        monitorMode,
        monitorOpen,
        monitorRetryKey,
        monitorStream,
        monitorVideoReady,
        stopMonitorStream,
        stopPlaybackStream
    ]);

    return (
        <section aria-label="device-management-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <ZhDetailHeader
                    leftInfos={[{ label: "编号", value: valueOrDefault(device?.serialNo) }]}
                    rightInfos={[{ label: "添加时间", value: formatDate(device?.createdTime) }]}
                    stateDesc={ONLINE.getIntroByCode(device?.online) || DEFAULT_VALUE}
                    title="设备详情"
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <Space className="mt-4" wrap>
                    <Link to={`/logistics/device/deviceManagement/edit/${device?.id ?? resolvedId}`}>
                        <Button disabled={isButtonBanned} type="primary">
                            编辑
                        </Button>
                    </Link>
                    {device ? (
                        <Button disabled={isButtonBanned} onClick={() => setPendingState(nextState)}>
                            {nextState === ABLE.ENABLE.code ? "启用" : "禁用"}
                        </Button>
                    ) : null}
                    {device ? (
                        <Button
                            danger
                            disabled={isButtonBanned}
                            onClick={() => {
                                setDeleteSerial("");
                                setDeleteValidationError("");
                                setDeleteDialogOpen(true);
                            }}
                        >
                            删除
                        </Button>
                    ) : null}
                    {canMonitor ? (
                        <Button loading={monitorLoading} onClick={() => void handleMonitor()}>
                            监控
                        </Button>
                    ) : null}
                    <Button onClick={() => navigate(-1)}>返回</Button>
                </Space>
                <div className="mb-4 mt-6 text-base font-medium">基础信息</div>
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="名称" value={valueOrDefault(device?.name)} />
                    <ZhBaseItem label="类型" value={DEVICE_TYPE.getIntroByCode(device?.type) || DEFAULT_VALUE} />
                    <ZhBaseItem label="是否在线" value={ONLINE.getIntroByCode(device?.online) || DEFAULT_VALUE} />
                    <ZhBaseItem label="序列号" value={valueOrDefault(device?.serialNo)} />
                    <ZhBaseItem label="ICCID" value={valueOrDefault(device?.dataCard)} />
                </ZhGrid>
                <div className="mb-4 mt-6 text-base font-medium">安装信息</div>
                <ZhGrid cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="关联船舶">
                        {shipName && ship?.id ? (
                            <Link to={`/logistics/shipManage/shipInfo/shipInfoDetail/${ship.id}`}>{shipName}</Link>
                        ) : (
                            DEFAULT_VALUE
                        )}
                    </ZhBaseItem>
                    <ZhBaseItem label="负责人" value={valueOrDefault(ship?.captain)} />
                    <ZhBaseItem label="联系方式" value={valueOrDefault(ship?.mobile)} />
                    <ZhBaseItem label="安装位置" value={valueOrDefault(device?.position)} />
                    <ZhBaseItem label="安装人" value={valueOrDefault(device?.installer)} />
                    <ZhBaseItem label="安装日期" value={valueOrDefault(device?.installDate)} />
                    <ZhBaseItem label="安装地点" value={valueOrDefault(device?.installSite)} />
                    <ZhBaseItem label="推介人" value={valueOrDefault(device?.referrer)} />
                    <ZhBaseItem label="添加人" value={valueOrDefault(detail?.user?.name)} />
                    <ZhBaseItem label="添加时间" value={formatDate(device?.createdTime)} />
                    <ZhBaseItem label="启用状态" value={ABLE.getIntroByCode(device?.state) || DEFAULT_VALUE} />
                </ZhGrid>
            </Card>
            <Modal
                open={pendingState != null}
                title="提示"
                okText="确认"
                cancelText="取消"
                confirmLoading={actionLoading}
                onOk={() => void handleStateConfirm()}
                onCancel={() => {
                    if (!actionLoading) setPendingState(undefined);
                }}
            >
                {pendingState === ABLE.ENABLE.code ? "是否启用该设备？" : "是否禁用该设备？"}
            </Modal>
            <Modal
                open={deleteDialogOpen}
                title="删除设备"
                okText="删除"
                cancelText="取消"
                confirmLoading={actionLoading}
                onOk={() => void handleDeleteConfirm()}
                onCancel={() => {
                    if (!actionLoading) setDeleteDialogOpen(false);
                }}
            >
                <Input
                    aria-label="序列号校验"
                    maxLength={9}
                    placeholder="请输入序列号校验"
                    value={deleteSerial}
                    onChange={event => {
                        setDeleteSerial(event.target.value);
                        setDeleteValidationError("");
                    }}
                />
                {deleteValidationError ? (
                    <Typography.Text type="danger">{deleteValidationError}</Typography.Text>
                ) : null}
            </Modal>
            <ZhDialog
                open={monitorOpen}
                title="监控调阅"
                width="842px"
                closeOnClickModal={false}
                closeOnPressEscape={false}
                destroyOnHidden
                footer={
                    <Space>
                        {monitorError ? (
                            <Button danger onClick={handleMonitorRetry}>
                                重新播放
                            </Button>
                        ) : null}
                        <Button loading={monitorLoading} onClick={() => void handleMonitorModeSwitch()}>
                            {monitorMode === "live" ? "视频回放" : "直播"}
                        </Button>
                        <Button onClick={() => setMonitorOpen(false)}>取消</Button>
                    </Space>
                }
                onCancel={() => setMonitorOpen(false)}
            >
                <Typography.Text>监控设备：{valueOrDefault(device?.serialNo)}</Typography.Text>
                {monitorStream?.playUrl ? (
                    <Typography.Paragraph>播放地址：{monitorStream.playUrl}</Typography.Paragraph>
                ) : null}
                {monitorStream?.liveId ? (
                    <Typography.Paragraph>
                        {monitorMode === "live" ? "直播" : "回放"}流 ID：{monitorStream.liveId}
                    </Typography.Paragraph>
                ) : null}
                {/* 保留 Vue videoDialog 的当前模式文案，便于用户确认正在查看直播或视频回放。 */}
                <Typography.Text>当前模式：{monitorMode === "live" ? "直播" : "视频回放"}</Typography.Text>
                {monitorMode === "playback" ? (
                    <Space className="w-full" direction="vertical" size="small">
                        <Typography.Text>播放状态：{playbackStateLabels[monitorPlaybackState]}</Typography.Text>
                        <label htmlFor="device-playback-timeline">回放时间轴</label>
                        <input
                            id="device-playback-timeline"
                            aria-label="回放时间轴"
                            className="w-full"
                            type="range"
                            min={0}
                            max={86399}
                            step={1}
                            value={playbackPosition}
                            disabled={monitorLoading}
                            onChange={event => void handlePlaybackTimelineChange(event)}
                        />
                        <Typography.Text type="secondary">
                            选中时间：{formatPlaybackSeconds(playbackPosition)}
                        </Typography.Text>
                    </Space>
                ) : null}
                <video ref={setMonitorVideoRef} data-testid="device-monitor-video" className="h-64 w-full" controls />
                <Typography.Paragraph>
                    监控窗口已打开，取流播放能力沿用 Vue video-dialog 契约继续接入。
                </Typography.Paragraph>
            </ZhDialog>
        </section>
    );
}
